import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Sparkles, Star, Heart, Calculator, Users, ArrowRight, Menu, X, CalendarIcon, Phone, Music, Volume2 } from 'lucide-react';
import { Footer } from '../components/Footer';
import SEO, { pageSEO } from '../components/SEO';
import { format } from 'date-fns';

// Music player hook - generates ambient mystical tones using Web Audio API
const useMusicPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoaded, setIsLoaded] = useState(true);
  const audioContextRef = useRef(null);
  const oscillatorsRef = useRef([]);
  const gainNodeRef = useRef(null);

  const startAmbientMusic = () => {
    if (audioContextRef.current) return;
    
    // Create audio context
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    audioContextRef.current = new AudioContext();
    const ctx = audioContextRef.current;
    
    // Master gain
    gainNodeRef.current = ctx.createGain();
    gainNodeRef.current.gain.value = 0.15;
    gainNodeRef.current.connect(ctx.destination);
    
    // Create layered ambient tones (mystical frequencies)
    const frequencies = [
      { freq: 174, type: 'sine' },    // Solfeggio - grounding
      { freq: 285, type: 'sine' },    // Solfeggio - healing
      { freq: 396, type: 'sine' },    // Solfeggio - liberation
      { freq: 432, type: 'sine' },    // Universal harmony
    ];
    
    frequencies.forEach(({ freq, type }, i) => {
      const osc = ctx.createOscillator();
      const oscGain = ctx.createGain();
      
      osc.type = type;
      osc.frequency.value = freq;
      oscGain.gain.value = 0.08 - (i * 0.015); // Decreasing volume for higher frequencies
      
      // Add subtle vibrato
      const lfo = ctx.createOscillator();
      const lfoGain = ctx.createGain();
      lfo.frequency.value = 0.1 + (i * 0.05);
      lfoGain.gain.value = 2;
      lfo.connect(lfoGain);
      lfoGain.connect(osc.frequency);
      lfo.start();
      
      osc.connect(oscGain);
      oscGain.connect(gainNodeRef.current);
      osc.start();
      
      oscillatorsRef.current.push({ osc, lfo, oscGain });
    });
  };

  const stopAmbientMusic = () => {
    oscillatorsRef.current.forEach(({ osc, lfo }) => {
      try {
        osc.stop();
        lfo.stop();
      } catch (e) {}
    });
    oscillatorsRef.current = [];
    
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  };

  const toggleMusic = () => {
    if (isPlaying) {
      stopAmbientMusic();
    } else {
      startAmbientMusic();
    }
    setIsPlaying(!isPlaying);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => stopAmbientMusic();
  }, []);

  return { isPlaying, isLoaded, toggleMusic };
};

// Life Path Number meanings - expanded descriptions
const lifePathMeanings = {
  1: { 
    title: "The Leader", 
    desc: "Independent, pioneering, and ambitious. You're here to lead and innovate.",
    expanded: "As a Life Path 1, you are a natural born leader with incredible drive and determination. You have original ideas and the courage to pursue them. Your independent spirit pushes you to forge your own path rather than follow others. You thrive when you can take initiative and make things happen on your own terms.",
    strengths: "Leadership, originality, courage, determination, independence",
    challenges: "Learning to collaborate, avoiding stubbornness, patience with others",
    loveStyle: "You need a partner who respects your independence while supporting your ambitions."
  },
  2: { 
    title: "The Mediator", 
    desc: "Diplomatic, sensitive, and cooperative. You're here to bring harmony and partnership.",
    expanded: "As a Life Path 2, you are the peacemaker and diplomat of the numerology world. You have a gift for seeing both sides of any situation and bringing people together. Your intuitive nature allows you to sense the emotions and needs of others. Partnerships and relationships are central to your life purpose.",
    strengths: "Diplomacy, intuition, cooperation, sensitivity, patience",
    challenges: "Avoiding over-sensitivity, standing up for yourself, making decisions",
    loveStyle: "You crave deep emotional connection and thrive in harmonious, loving partnerships."
  },
  3: { 
    title: "The Communicator", 
    desc: "Creative, expressive, and social. You're here to inspire and uplift others.",
    expanded: "As a Life Path 3, you are blessed with the gift of creative expression. Whether through words, art, music, or performance, you have a natural ability to communicate and inspire others. Your optimistic and joyful nature draws people to you. You bring light and laughter wherever you go.",
    strengths: "Creativity, communication, optimism, charisma, artistic talent",
    challenges: "Focus and discipline, avoiding scattered energy, finishing projects",
    loveStyle: "You need a partner who appreciates your creative spirit and keeps up with your social nature."
  },
  4: { 
    title: "The Builder", 
    desc: "Practical, reliable, and hardworking. You're here to create lasting foundations.",
    expanded: "As a Life Path 4, you are the foundation builder of the numerology world. You have an incredible work ethic and the patience to see projects through to completion. Your practical, methodical approach creates stability and security for yourself and others. You value honesty, loyalty, and tradition.",
    strengths: "Reliability, hard work, organization, loyalty, practicality",
    challenges: "Flexibility, embracing change, avoiding rigidity, relaxation",
    loveStyle: "You seek a stable, committed partner who shares your values of loyalty and building a secure future."
  },
  5: { 
    title: "The Freedom Seeker", 
    desc: "Adventurous, versatile, and dynamic. You're here to experience life fully.",
    expanded: "As a Life Path 5, you are here to experience all that life has to offer. Freedom is essential to your well-being, and you thrive on change, adventure, and variety. Your versatile nature allows you to adapt to any situation. You are a natural explorer, always seeking new experiences and knowledge.",
    strengths: "Adaptability, adventure, versatility, curiosity, resourcefulness",
    challenges: "Commitment, focus, avoiding restlessness, moderation",
    loveStyle: "You need a partner who gives you space to explore while sharing in your adventures."
  },
  6: { 
    title: "The Nurturer", 
    desc: "Responsible, caring, and harmonious. You're here to love and heal others.",
    expanded: "As a Life Path 6, you are the cosmic parent and healer. Your heart is filled with love and compassion for others, especially family and loved ones. You have a strong sense of responsibility and a natural desire to care for those around you. Creating beauty, harmony, and balance is essential to your soul.",
    strengths: "Nurturing, responsibility, love, healing, domesticity",
    challenges: "Avoiding over-giving, self-care, letting others learn their lessons",
    loveStyle: "You are devoted and loving, seeking a partner who values family and emotional connection."
  },
  7: { 
    title: "The Seeker", 
    desc: "Analytical, spiritual, and introspective. You're here to seek deeper truths.",
    expanded: "As a Life Path 7, you are the spiritual seeker and truth-finder. You have a brilliant analytical mind combined with deep intuition. Solitude and reflection are necessary for your well-being. You are drawn to life's mysteries and have a natural gift for research, analysis, and uncovering hidden knowledge.",
    strengths: "Intuition, analysis, spirituality, wisdom, introspection",
    challenges: "Opening up emotionally, trusting others, avoiding isolation",
    loveStyle: "You need a partner who respects your need for solitude and shares your love of meaningful conversation."
  },
  8: { 
    title: "The Achiever", 
    desc: "Ambitious, authoritative, and successful. You're here to manifest abundance.",
    expanded: "As a Life Path 8, you are here to master the material world and achieve success. You have natural business acumen and leadership abilities. Power, authority, and financial abundance are themes in your life. When balanced, you use your success to help others and make a positive impact on the world.",
    strengths: "Ambition, business sense, leadership, manifestation, authority",
    challenges: "Work-life balance, avoiding materialism, using power wisely",
    loveStyle: "You need a partner who supports your ambitions and can handle your powerful presence."
  },
  9: { 
    title: "The Humanitarian", 
    desc: "Compassionate, wise, and generous. You're here to serve humanity.",
    expanded: "As a Life Path 9, you are the old soul with a global perspective. Your compassionate heart extends beyond personal relationships to embrace all of humanity. You have wisdom beyond your years and a natural understanding of the human condition. Letting go and forgiveness are important themes in your journey.",
    strengths: "Compassion, wisdom, generosity, humanitarianism, creativity",
    challenges: "Letting go of the past, personal boundaries, avoiding martyrdom",
    loveStyle: "You seek a partner who shares your humanitarian values and supports your higher calling."
  },
  11: { 
    title: "The Intuitive", 
    desc: "Visionary, inspiring, and enlightened. You're a master number with spiritual gifts.",
    expanded: "As a Life Path 11, you carry the energy of a Master Number. You are highly intuitive, almost psychic, with the ability to channel spiritual insights. You are here to inspire and illuminate others with your vision. The challenge is balancing your spiritual sensitivity with practical life demands.",
    strengths: "Intuition, inspiration, spiritual insight, charisma, vision",
    challenges: "Nervous energy, self-doubt, staying grounded, high sensitivity",
    loveStyle: "You need a spiritually aware partner who understands your sensitivity and supports your higher purpose."
  },
  22: { 
    title: "The Master Builder", 
    desc: "Powerful practical visionary. You're here to build something great for humanity.",
    expanded: "As a Life Path 22, you carry the most powerful Master Number. You have the vision of the 11 combined with the practical ability of the 4. You are capable of turning the grandest dreams into reality. Your purpose is to build something lasting that benefits humanity on a large scale.",
    strengths: "Vision, manifestation, practical genius, leadership, large-scale thinking",
    challenges: "Enormous pressure, perfectionism, managing high expectations",
    loveStyle: "You need a partner who believes in your grand vision and supports your mission."
  },
  33: { 
    title: "The Master Teacher", 
    desc: "Nurturing, selfless, and deeply spiritual. You're here to uplift consciousness.",
    expanded: "As a Life Path 33, you embody the highest Master Number - the Master Teacher. You combine the nurturing energy of 6 with profound spiritual wisdom. Your purpose is to heal and uplift humanity through unconditional love and compassion. You are a beacon of light for others.",
    strengths: "Unconditional love, spiritual mastery, healing, teaching, selflessness",
    challenges: "Carrying the weight of others, maintaining boundaries, self-care",
    loveStyle: "You seek a deeply spiritual connection with a partner who shares your devotion to healing and love."
  }
};

const reduceToSingleDigit = (num) => {
  while (num > 9 && num !== 11 && num !== 22 && num !== 33) {
    num = String(num).split('').reduce((a, b) => a + parseInt(b), 0);
  }
  return num;
};

const calculateLifePath = (date) => {
  const dateStr = format(date, 'yyyyMMdd');
  const total = dateStr.split('').reduce((a, b) => a + parseInt(b), 0);
  return reduceToSingleDigit(total);
};

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8 }
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.15
    }
  }
};

// Swirl SVG Component
const SwirlDecoration = ({ className, style }) => (
  <svg 
    className={className} 
    style={style}
    viewBox="0 0 200 200" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M100 20 Q140 40 120 80 Q100 120 140 140 Q180 160 160 180" 
      stroke="url(#swirl-gradient)" 
      strokeWidth="3" 
      fill="none"
      strokeLinecap="round"
    />
    <path 
      d="M80 40 Q40 60 60 100 Q80 140 40 160" 
      stroke="url(#swirl-gradient-2)" 
      strokeWidth="2" 
      fill="none"
      strokeLinecap="round"
    />
    <defs>
      <linearGradient id="swirl-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.6" />
        <stop offset="50%" stopColor="#D4AF37" stopOpacity="0.8" />
        <stop offset="100%" stopColor="#EC4899" stopOpacity="0.6" />
      </linearGradient>
      <linearGradient id="swirl-gradient-2" x1="100%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.5" />
        <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0.5" />
      </linearGradient>
    </defs>
  </svg>
);

// Sparkle Star Component
const SparkleStars = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(20)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
        }}
        animate={{
          scale: [1, 1.5, 1],
          opacity: [0.3, 1, 0.3],
        }}
        transition={{
          duration: 2 + Math.random() * 2,
          repeat: Infinity,
          delay: Math.random() * 2,
        }}
      >
        <Sparkles className="w-4 h-4 text-[#D4AF37]" />
      </motion.div>
    ))}
  </div>
);

export const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [birthday, setBirthday] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [lifePathResult, setLifePathResult] = useState(null);
  const { isPlaying, isLoaded, toggleMusic } = useMusicPlayer();

  const handleCalculateLifePath = () => {
    if (birthday) {
      const lifePath = calculateLifePath(birthday);
      setLifePathResult(lifePath);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden swirl-bg">
      <SEO {...pageSEO.home} />
      
      {/* Phone Bar */}
      <div className="bg-gradient-to-r from-[#5B21B6] to-[#8B5CF6] text-white py-2 px-4 flex items-center justify-center relative z-30">
        <a 
          href="tel:678-665-4712" 
          className="inline-flex items-center gap-2 font-bold hover:text-[#D4AF37] transition-colors"
          data-testid="landing-phone-bar"
        >
          <Phone className="w-4 h-4" />
          Call Karine: 678-665-4712
        </a>
      </div>
      
      {/* Animated Background Elements */}
      <SparkleStars />
      
      {/* Swirl Decorations */}
      <SwirlDecoration 
        className="absolute top-10 left-10 w-40 h-40 opacity-60 animate-float" 
        style={{ animationDelay: '0s' }}
      />
      <SwirlDecoration 
        className="absolute top-1/4 right-10 w-60 h-60 opacity-50 animate-float" 
        style={{ animationDelay: '1s', transform: 'rotate(45deg)' }}
      />
      <SwirlDecoration 
        className="absolute bottom-20 left-1/4 w-48 h-48 opacity-40 animate-float" 
        style={{ animationDelay: '2s', transform: 'rotate(-30deg)' }}
      />
      <SwirlDecoration 
        className="absolute bottom-40 right-1/3 w-32 h-32 opacity-50 animate-float" 
        style={{ animationDelay: '1.5s', transform: 'rotate(90deg)' }}
      />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        {/* Background Gradient Orbs */}
        <div className="absolute top-20 left-20 w-96 h-96 bg-purple-400/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-amber-400/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-pink-400/10 rounded-full blur-3xl" />

        {/* Navigation */}
        <nav className="absolute top-0 left-0 right-0 z-20 px-6 md:px-12 py-6">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 md:gap-4" data-testid="logo">
              <motion.div 
                className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-purple-500 via-amber-400 to-pink-500 flex items-center justify-center glow-purple"
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <Sparkles className="w-6 h-6 md:w-8 md:h-8 text-white" />
              </motion.div>
              <span className="font-mystical text-2xl md:text-4xl lg:text-5xl font-bold tracking-wide" style={{ color: '#5B21B6' }}>Intuitive Matchmaker</span>
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              <Link 
                to="/services" 
                className="text-[#5B21B6] hover:text-[#D4AF37] transition-colors font-semibold text-lg"
                data-testid="nav-services"
              >
                Services
              </Link>
              <Link 
                to="/calculator" 
                className="text-[#5B21B6] hover:text-[#D4AF37] transition-colors font-semibold text-lg"
                data-testid="nav-calculator"
              >
                Calculator
              </Link>
              <Link 
                to="/contact" 
                className="text-[#5B21B6] hover:text-[#D4AF37] transition-colors font-semibold text-lg"
                data-testid="nav-contact"
              >
                Contact
              </Link>
              <Link 
                to="/testimonials" 
                className="text-[#5B21B6] hover:text-[#D4AF37] transition-colors font-semibold text-lg"
                data-testid="nav-testimonials"
              >
                Testimonials
              </Link>
              <Link 
                to="/blog" 
                className="text-[#5B21B6] hover:text-[#D4AF37] transition-colors font-semibold text-lg"
                data-testid="nav-blog"
              >
                Blog
              </Link>
              <Link 
                to="/about-numerology" 
                className="text-[#5B21B6] hover:text-[#D4AF37] transition-colors font-semibold text-lg"
                data-testid="nav-about-numerology"
              >
                About
              </Link>
              <Link to="/register" data-testid="nav-register">
                <Button className="rounded-full btn-magic px-6 py-3 text-base font-bold">
                  Join Free Database
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-full bg-white/80 backdrop-blur-sm border border-purple-200 text-[#5B21B6]"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
                className="md:hidden fixed inset-0 top-[108px] bg-white/98 backdrop-blur-xl border-b border-purple-200 shadow-xl z-50 overflow-y-auto"
              >
                <div className="flex flex-col p-6 space-y-4">
                  <Link 
                    to="/services" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-[#5B21B6] hover:text-[#D4AF37] transition-colors font-semibold text-lg py-2 border-b border-purple-100"
                    data-testid="mobile-nav-services"
                  >
                    Services
                  </Link>
                  <Link 
                    to="/calculator" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-[#5B21B6] hover:text-[#D4AF37] transition-colors font-semibold text-lg py-2 border-b border-purple-100"
                    data-testid="mobile-nav-calculator"
                  >
                    Soulmate Calculator
                  </Link>
                  <Link 
                    to="/contact" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-[#5B21B6] hover:text-[#D4AF37] transition-colors font-semibold text-lg py-2 border-b border-purple-100"
                    data-testid="mobile-nav-contact"
                  >
                    Contact
                  </Link>
                  <Link 
                    to="/testimonials" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-[#5B21B6] hover:text-[#D4AF37] transition-colors font-semibold text-lg py-2 border-b border-purple-100"
                    data-testid="mobile-nav-testimonials"
                  >
                    Testimonials
                  </Link>
                  <Link 
                    to="/blog" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-[#5B21B6] hover:text-[#D4AF37] transition-colors font-semibold text-lg py-2 border-b border-purple-100"
                    data-testid="mobile-nav-blog"
                  >
                    Blog
                  </Link>
                  <Link 
                    to="/about-numerology" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-[#5B21B6] hover:text-[#D4AF37] transition-colors font-semibold text-lg py-2 border-b border-purple-100"
                    data-testid="mobile-nav-about-numerology"
                  >
                    About Numerology
                  </Link>
                  <Link to="/register" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-register">
                    <Button className="w-full rounded-full btn-magic px-8 py-4 text-lg font-bold mt-2">
                      Join Free Database
                    </Button>
                  </Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20">
          <motion.div
            initial="initial"
            animate="animate"
            variants={stagger}
            className="max-w-4xl"
          >
            <motion.p 
              variants={fadeInUp}
              className="font-whimsy text-xl md:text-2xl text-[#EC4899] mb-4 font-bold"
            >
              ✨ Connecting Hearts with Intuition ✨
            </motion.p>
            
            <motion.h1 
              variants={fadeInUp}
              className="font-mystical text-5xl md:text-7xl lg:text-8xl text-[#3D2A4D] tracking-tight leading-[1.05] mb-8 font-bold"
            >
              Discover Your
              <span className="block text-magic-gradient font-bold">Destined Love Connection</span>
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-lg md:text-xl text-[#5D4A6D] font-medium leading-relaxed mb-10 max-w-2xl"
            >
              Uncover the mystical bonds between souls through the ancient wisdom of 
              <span className="font-whimsy text-[#EC4899] text-xl md:text-2xl font-bold"> Pythagorean numerology</span>. 
              Your numbers reveal your destiny.
            </motion.p>
            
            <motion.div 
              variants={fadeInUp}
              className="flex flex-wrap gap-6"
            >
              <Link to="/register" data-testid="hero-get-started">
                <Button className="rounded-full btn-magic px-6 sm:px-8 py-6 text-base sm:text-lg font-bold shadow-2xl whitespace-normal text-center leading-tight">
                  Join the Free Numerology Database
                  <ArrowRight className="ml-2 w-5 h-5 flex-shrink-0" />
                </Button>
              </Link>
              <Link to="/calculator" data-testid="hero-calculator">
                <Button 
                  variant="outline" 
                  className="rounded-full px-8 py-6 text-lg font-bold border-2 border-[#8B5CF6] text-[#8B5CF6] hover:bg-[#8B5CF6]/10 hover:border-[#D4AF37]"
                >
                  <Calculator className="mr-2 w-5 h-5" />
                  Soulmate Calculator
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Floating Numbers */}
        <div className="absolute right-8 md:right-16 top-1/2 -translate-y-1/2 hidden lg:flex flex-col gap-8">
          {[1, 5, 9, 3, 7].map((num, i) => (
            <motion.div
              key={num}
              initial={{ opacity: 0, x: 100, rotate: -180 }}
              animate={{ opacity: 1, x: 0, rotate: 0 }}
              transition={{ delay: 0.8 + i * 0.15, duration: 0.8, type: "spring" }}
              className="number-circle"
              style={{ animationDelay: `${i * 0.8}s` }}
            >
              {num}
            </motion.div>
          ))}
        </div>
      </section>

      {/* Inspirational Quote Section */}
      <section className="py-12 relative">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="ethereal-card rounded-3xl p-8 md:p-12 relative overflow-hidden">
              {/* Decorative elements */}
              <div className="absolute top-4 left-4 text-6xl text-[#D4AF37]/20 font-serif">"</div>
              <div className="absolute bottom-4 right-4 text-6xl text-[#D4AF37]/20 font-serif rotate-180">"</div>
              
              <p className="font-whimsy text-xl md:text-2xl lg:text-3xl text-[#3D2A4D] leading-relaxed relative z-10">
                <span className="text-[#8B5CF6] font-bold">You will no longer have to worry</span> when life is throwing you curve balls. 
                You will no longer wonder why your relationships are suffering or not coming together the way you hoped for. 
                <span className="text-[#D4AF37] font-bold"> Let's make sense of it all.</span>
              </p>
              
              <div className="mt-6 flex items-center justify-center gap-2">
                <Heart className="w-5 h-5 text-pink-500 fill-pink-500" />
                <span className="text-[#6D5A7D] font-medium">— Karine Burt</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Meet Karine Section */}
      <section className="py-20 relative">
        {/* Subtle Gold Hearts */}
        <div className="subtle-gold-hearts">
          {[...Array(16)].map((_, i) => (
            <div key={i} className="subtle-heart">
              <Heart className="w-full h-full fill-current" />
            </div>
          ))}
        </div>
        
        <div className="max-w-5xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="ethereal-card rounded-[2rem] p-8 md:p-12"
          >
            <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
              {/* Profile Image */}
              <div className="flex-shrink-0">
                <div className="w-56 h-56 md:w-72 md:h-72 rounded-full overflow-hidden border-4 border-[#D4AF37] glow-gold shadow-2xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_soul-match-numbers/artifacts/lem5lq0f_IMG_4333.jpeg" 
                    alt="Karine - Psychic Medium Numerology Matchmaker"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: '50% 25%' }}
                  />
                </div>
              </div>
              
              {/* Info */}
              <div className="text-center md:text-left flex-1">
                <p className="font-whimsy text-xl md:text-2xl text-[#8B5CF6] mb-2">Your Guide</p>
                <h2 className="font-mystical text-3xl md:text-4xl text-[#2D1B3D] font-bold mb-4">
                  Karine Burt
                </h2>
                <p className="font-whimsy text-xl md:text-2xl text-[#8B5CF6] font-bold mb-6">
                  Psychic Medium, Numerology Matchmaker & Gifted Intuitive
                </p>
                <div className="text-[#4A3A5A] text-lg mb-8 leading-relaxed space-y-4">
                  <p>
                    I have a profound connection to the spiritual realm, perceiving energies and embracing the gifts of 
                    <span className="text-[#8B5CF6] font-semibold"> clairvoyance, clairsentience, and clairaudience</span>. 
                    Divine guidance flows through me, allowing me to connect with God, the Source of all that is. Messages from 
                    transitioned loved ones often come through, offering comfort and valuable insights. Numerology serves as a 
                    tool that deepens my understanding and enhances the connections I create.
                  </p>
                  <p>
                    With a passion for making meaningful connections, I often say, 
                    <span className="text-[#D4AF37] font-bold"> "I am much much more than a Matchmaker"</span>. 
                    I love helping individuals change their lives while discovering fulfilling relationships — and that brings me great joy.
                  </p>
                  <p>
                    As a gifted Intuitive, I help souls find clarity, healing, and new beginnings. Are you carrying the weight of 
                    past relationships? I can help you uncover hidden truths, find closure, and finally put those old connections 
                    to rest — freeing your heart to embrace the love you truly deserve. Let's connect on your unique path, 
                    uncovering beauty and clarity in your journey as we co-create a brighter future for you together.
                  </p>
                </div>
                <p className="text-[#4A3A5A] text-base mb-6">
                  Also founder of <a href="https://www.savvysingles.com" target="_blank" rel="noopener noreferrer" className="text-[#8B5CF6] font-bold hover:text-[#D4AF37] underline">SavvySingles.com</a> — A+ Rated with the BBB ⭐
                </p>
                
                {/* Contact Info */}
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-4">
                    <a 
                      href="tel:678-665-4712" 
                      className="inline-flex items-center gap-2 bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white px-6 py-3 rounded-full font-bold hover:shadow-lg hover:scale-105 transition-all"
                      data-testid="call-karine-btn"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      678-665-4712
                    </a>
                    <a 
                      href="mailto:karine@intuitivematchmaker.com" 
                      className="inline-flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#B8860B] text-white px-6 py-3 rounded-full font-bold hover:shadow-lg hover:scale-105 transition-all"
                      data-testid="email-karine-btn"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      karine@intuitivematchmaker.com
                    </a>
                  </div>
                  
                  {/* Social Media */}
                  <div className="flex items-center justify-center md:justify-start gap-4 mt-2">
                    <a 
                      href="https://www.facebook.com/themystictellers" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#1877F2] text-white px-5 py-3 rounded-full font-bold hover:shadow-lg hover:scale-105 transition-all"
                      data-testid="facebook-btn"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                      Facebook
                    </a>
                    <a 
                      href="https://www.instagram.com/themystictellers" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-gradient-to-r from-[#833AB4] via-[#E1306C] to-[#F77737] text-white px-5 py-3 rounded-full font-bold hover:shadow-lg hover:scale-105 transition-all"
                      data-testid="instagram-btn"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                      Instagram
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Life Path Calculator Section */}
      <section className="py-20 relative">
        <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <p className="font-whimsy text-2xl text-[#EC4899] mb-4 font-bold">
              ~ Discover Your Number ~
            </p>
            <h2 className="font-mystical text-3xl sm:text-4xl md:text-5xl text-[#3D2A4D] font-bold mb-6">
              What's Your Life Path?
            </h2>
            
            {/* Karine's Message */}
            <div className="bg-white/50 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-8 border border-purple-100 max-w-3xl mx-auto">
              <p className="text-[#3D2A4D] text-lg md:text-xl leading-relaxed font-medium italic">
                "Numerology is God's Language. Once you learn your core numbers you will be set free to then accomplish your mission in life. No more wandering aimlessly. You will finally get to view yourself in a whole different mirror."
              </p>
              <p className="text-[#8B5CF6] font-whimsy text-lg mt-3">— Karine Burt</p>
            </div>
            
            <p className="text-[#5D4A6D] text-lg max-w-xl mx-auto">
              Your Life Path Number reveals your soul's purpose. Enter your birthday to discover yours!
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="ethereal-card rounded-3xl p-8 md:p-10"
          >
            <div className="flex flex-col items-center gap-6">
              {/* Birthday Picker */}
              <div className="w-full max-w-sm">
                <label className="block text-[#3D2A4D] font-semibold text-lg mb-3 text-center">
                  Select Your Birthday
                </label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full h-14 justify-center text-center font-medium bg-white/70 border-purple-200 hover:border-[#8B5CF6] rounded-xl text-lg"
                      data-testid="lifepath-birthday-trigger"
                    >
                      <CalendarIcon className="mr-3 h-5 w-5 text-[#8B5CF6]" />
                      {birthday ? (
                        <span className="text-[#2D1B3D]">{format(birthday, 'MMMM d, yyyy')}</span>
                      ) : (
                        <span className="text-[#8C7A9C]">Choose your birthday</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white border-purple-200" align="center">
                    <Calendar
                      mode="single"
                      selected={birthday}
                      onSelect={(date) => {
                        setBirthday(date);
                        setCalendarOpen(false);
                        setLifePathResult(null);
                      }}
                      disabled={(date) => date > new Date() || date < new Date('1920-01-01')}
                      captionLayout="dropdown-buttons"
                      fromYear={1920}
                      toYear={new Date().getFullYear()}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Calculate Button */}
              <Button
                onClick={handleCalculateLifePath}
                disabled={!birthday}
                className="rounded-full btn-magic px-10 py-6 text-xl font-bold"
                data-testid="calculate-lifepath-btn"
              >
                <Star className="w-6 h-6 mr-2" />
                Reveal My Life Path
              </Button>

              {/* Result */}
              <AnimatePresence>
                {lifePathResult && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    className="w-full mt-4"
                  >
                    <div className="bg-gradient-to-br from-[#8B5CF6]/10 via-[#D4AF37]/10 to-[#EC4899]/10 rounded-3xl p-8 md:p-10 border border-purple-200">
                      {/* Number Display */}
                      <div className="text-center mb-8">
                        <p className="text-[#6D5A7D] font-medium mb-3">Your Life Path Number is</p>
                        <div className="w-28 h-28 rounded-full bg-gradient-to-br from-purple-500 via-amber-400 to-pink-500 flex items-center justify-center mx-auto mb-4 glow-purple shadow-xl">
                          <span className="font-mystical text-6xl text-white font-bold">{lifePathResult}</span>
                        </div>
                        <h3 className="font-mystical text-4xl text-[#2D1B3D] font-bold">
                          {lifePathMeanings[lifePathResult]?.title}
                        </h3>
                      </div>

                      {/* Expanded Description */}
                      <div className="bg-white/60 rounded-2xl p-6 mb-6">
                        <p className="text-[#3D2A4D] text-lg leading-relaxed">
                          {lifePathMeanings[lifePathResult]?.expanded}
                        </p>
                      </div>

                      {/* Strengths & Challenges */}
                      <div className="grid md:grid-cols-2 gap-4 mb-6">
                        <div className="bg-white/60 rounded-2xl p-5">
                          <h4 className="font-mystical text-xl text-[#2D1B3D] font-bold mb-2 flex items-center gap-2">
                            <Star className="w-5 h-5 text-[#D4AF37]" />
                            Your Strengths
                          </h4>
                          <p className="text-[#5D4A6D]">
                            {lifePathMeanings[lifePathResult]?.strengths}
                          </p>
                        </div>
                        <div className="bg-white/60 rounded-2xl p-5">
                          <h4 className="font-mystical text-xl text-[#2D1B3D] font-bold mb-2 flex items-center gap-2">
                            <Heart className="w-5 h-5 text-[#EC4899]" />
                            Growth Areas
                          </h4>
                          <p className="text-[#5D4A6D]">
                            {lifePathMeanings[lifePathResult]?.challenges}
                          </p>
                        </div>
                      </div>

                      {/* Love Style */}
                      <div className="bg-gradient-to-r from-[#EC4899]/20 to-[#8B5CF6]/20 rounded-2xl p-5 mb-6">
                        <h4 className="font-mystical text-xl text-[#2D1B3D] font-bold mb-2 flex items-center gap-2">
                          <Heart className="w-5 h-5 text-[#EC4899] fill-[#EC4899]" />
                          In Love
                        </h4>
                        <p className="text-[#3D2A4D] text-lg">
                          {lifePathMeanings[lifePathResult]?.loveStyle}
                        </p>
                      </div>

                      {/* CTA */}
                      <div className="text-center pt-4 border-t border-purple-200">
                        <p className="text-[#8B5CF6] font-semibold text-lg mb-4">
                          Want to discover your Expression, Soul Urge & Personality numbers?
                        </p>
                        <Link to="/contact" data-testid="lifepath-cta">
                          <Button className="rounded-full btn-magic px-8 py-5 text-lg font-bold">
                            Book a Full Reading with Karine
                            <ArrowRight className="ml-2 w-5 h-5" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Life Path Compatibility Section */}
      <section className="py-20 relative">
        <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="font-whimsy text-2xl text-[#EC4899] mb-4 font-bold">
              ~ Love & Numbers ~
            </p>
            <h2 className="font-mystical text-3xl sm:text-4xl md:text-5xl text-[#3D2A4D] font-bold mb-6">
              Which Life Path Numbers Are Most Compatible in Love?
            </h2>
            <p className="text-[#5D4A6D] text-lg max-w-2xl mx-auto">
              Compatibility in numerology is not about finding someone with the same number — it is about finding complementary energies.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            {[
              { num: 1, title: "Life Path 1", matches: "3, 5, 6", desc: "Needs admiration and independence" },
              { num: 2, title: "Life Path 2", matches: "6, 8, 9", desc: "Needs emotional security and deep connection" },
              { num: 3, title: "Life Path 3", matches: "1, 5, 7", desc: "Needs partners who appreciate their expressive nature" },
              { num: 4, title: "Life Path 4", matches: "1, 7, 8", desc: "Needs someone who values hard work and loyalty" },
              { num: 5, title: "Life Path 5", matches: "1, 3, 7", desc: "Needs partners who won't try to cage them" },
              { num: 6, title: "Life Path 6", matches: "1, 2, 9", desc: "Needs to feel needed and appreciated" },
              { num: 7, title: "Life Path 7", matches: "3, 5, 4", desc: "Needs intellectual and spiritual connection" },
              { num: 8, title: "Life Path 8", matches: "2, 4, 6", desc: "Needs supportive partners who understand their drive" },
              { num: 9, title: "Life Path 9", matches: "2, 3, 6", desc: "Needs partners who share their compassion" },
            ].map((item, i) => (
              <motion.div
                key={item.num}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="ethereal-card rounded-2xl p-5 hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] flex items-center justify-center text-white font-bold text-xl font-mystical">
                    {item.num}
                  </div>
                  <div>
                    <h3 className="font-mystical text-lg text-[#2D1B3D] font-bold">{item.title}</h3>
                    <p className="text-[#EC4899] font-semibold text-sm">Best with: {item.matches}</p>
                  </div>
                </div>
                <p className="text-[#5D4A6D] text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-10"
          >
            <p className="text-[#8B5CF6] font-semibold text-lg mb-4">
              Master Numbers 11, 22, and 33 have unique compatibility patterns due to their heightened spiritual energy.
            </p>
            <p className="text-[#5D4A6D] mb-6">
              While Life Path compatibility is important, your Expression Number, Soul Urge Number, and Chinese Zodiac sign all play a role.
            </p>
            <Link to="/calculator" data-testid="compatibility-cta">
              <Button className="rounded-full btn-magic px-8 py-5 text-lg font-bold">
                Try the Soulmate Calculator
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-32 relative">
        <div className="absolute inset-0 stars-bg opacity-30" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <p className="font-whimsy text-2xl md:text-3xl text-[#D4AF37] mb-4 font-bold">
              ~ The Sacred Numbers ~
            </p>
            <h2 className="font-mystical text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-[#3D2A4D] font-bold">
              Your Numerology Profile
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { 
                icon: Star, 
                title: 'Life Path', 
                desc: 'Your soul\'s purpose and the journey you\'re meant to walk in this lifetime.',
                color: 'from-purple-500 to-purple-600'
              },
              { 
                icon: Sparkles, 
                title: 'Expression', 
                desc: 'Your natural talents, abilities, and the gifts you bring to the world.',
                color: 'from-amber-400 to-amber-500'
              },
              { 
                icon: Heart, 
                title: 'Soul Urge', 
                desc: 'Your deepest desires, motivations, and what truly fulfills your heart.',
                color: 'from-pink-500 to-pink-600'
              },
              { 
                icon: Users, 
                title: 'Personality', 
                desc: 'How others perceive you and the energy you project outward.',
                color: 'from-violet-500 to-violet-600'
              },
            ].map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 40, rotate: -5 }}
                whileInView={{ opacity: 1, y: 0, rotate: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.6 }}
                className="ethereal-card rounded-3xl p-8 text-center group"
              >
                <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${feature.color} flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-all duration-500 shadow-lg`}>
                  <feature.icon className="w-10 h-10 text-white" />
                </div>
                <h3 className="font-mystical text-3xl text-[#3D2A4D] mb-4 font-bold">{feature.title}</h3>
                <p className="text-[#6D5A7D] font-medium text-xl leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-32 relative">
        <SwirlDecoration className="absolute top-20 right-20 w-64 h-64 opacity-30" />
        <SwirlDecoration className="absolute bottom-20 left-20 w-48 h-48 opacity-30" style={{ transform: 'rotate(180deg)' }} />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <p className="font-whimsy text-2xl md:text-3xl text-[#EC4899] mb-4 font-bold">
              ~ The Journey ~
            </p>
            <h2 className="font-mystical text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-[#3D2A4D] font-bold">
              How Intuitive Matchmaker Works
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-16">
            {[
              { step: '01', title: 'Create Your Profile', desc: 'Enter your full birth name and date to calculate your unique numerology numbers.' },
              { step: '02', title: 'Discover Matches', desc: 'Our algorithm finds souls whose numbers harmonize beautifully with yours.' },
              { step: '03', title: 'AI-Powered Readings', desc: 'Receive personalized compatibility readings that reveal your destined love connection.' },
            ].map((item, i) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.2 }}
                className="relative text-center"
              >
                <span className="font-mystical text-[140px] text-magic-gradient opacity-30 absolute -top-10 left-1/2 -translate-x-1/2 font-bold">
                  {item.step}
                </span>
                <div className="relative pt-20">
                  <h3 className="font-mystical text-4xl text-[#3D2A4D] mb-6 font-bold">{item.title}</h3>
                  <p className="text-[#6D5A7D] font-medium text-xl leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-amber-400/10 to-pink-500/10" />
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="ethereal-card rounded-[3rem] p-12 md:p-20"
          >
            <motion.div 
              className="w-28 h-28 rounded-full bg-gradient-to-br from-purple-500 via-amber-400 to-pink-500 flex items-center justify-center mx-auto mb-10 glow-purple"
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            >
              <Sparkles className="w-14 h-14 text-white" />
            </motion.div>
            <h2 className="font-mystical text-3xl sm:text-4xl md:text-5xl text-[#3D2A4D] mb-8 font-bold">
              Ready to Discover Your Cosmic Match?
            </h2>
            <p className="text-[#6D5A7D] font-medium text-lg md:text-xl mb-12 max-w-2xl mx-auto leading-relaxed">
              Join thousands of souls who have found meaningful connections through the 
              <span className="font-whimsy text-[#D4AF37] text-xl md:text-2xl font-bold"> ancient wisdom</span> of Pythagorean numerology.
            </p>
            <Link to="/register" data-testid="cta-get-started">
              <Button className="rounded-full btn-magic px-12 py-7 text-xl font-medium shadow-2xl">
                Start Your Journey Free
                <ArrowRight className="ml-3 w-6 h-6" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
